import { decode } from 'blurhash';

/**
 * Decode a blurhash into a small data-URL `<img>` source. Used for blurred
 * loading placeholders — most notably as `PopImage`'s `blurDataURL`, and by the
 * post-link card where the server returns a media snapshot with a blurhash but
 * no resolvable image URL.
 *
 * Decodes at a low resolution and lets the browser scale up — at the sizes
 * blurhashes target (covers, chat cards, list thumbs) the blur is
 * indistinguishable. Results are memoised per `blurhash + size` (decode + canvas
 * encode is the expensive part) in a small LRU so a scrolling list doesn't
 * re-decode on every render, while the cache can't grow without bound.
 *
 * SSR-safe: returns `undefined` when `document` is missing (canvas is required),
 * so callers gate the placeholder on the client.
 */

/** Bound the memo so a long-lived session with many distinct images stays flat. */
const MAX_CACHE_ENTRIES = 100;
const kCache = new Map<string, string>();

/** Read + promote to most-recently-used (Map preserves insertion order). */
function readCache(key: string): string | undefined {
  const cached = kCache.get(key);
  if (cached === undefined) return undefined;
  kCache.delete(key);
  kCache.set(key, cached);
  return cached;
}

/** Write, evicting the least-recently-used entry once at capacity. */
function writeCache(key: string, value: string): void {
  if (kCache.size >= MAX_CACHE_ENTRIES) {
    const oldest = kCache.keys().next().value;
    if (oldest !== undefined) {
      kCache.delete(oldest);
    }
  }
  kCache.set(key, value);
}

export function blurhashToDataUrl(
  blurhash: string | undefined,
  width = 32,
  height = 32,
): string | undefined {
  if (!blurhash) return undefined;
  const cacheKey = `${blurhash}:${width}x${height}`;
  const cached = readCache(cacheKey);
  if (cached) return cached;
  if (typeof document === 'undefined') return undefined;
  try {
    const pixels = decode(blurhash, width, height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);
    const dataUrl = canvas.toDataURL();
    writeCache(cacheKey, dataUrl);
    return dataUrl;
  } catch {
    return undefined;
  }
}
